import React, { useState, useContext, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "../App";
import { toast } from "sonner";
import { Music, ArrowLeft, Shield, User, Eye, EyeOff } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";

// Password strength calculator
const calculatePasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '' };
  
  let score = 0;
  const checks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    numbers: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    longLength: password.length >= 12
  };
  
  if (checks.length) score += 1;
  if (checks.lowercase) score += 1;
  if (checks.uppercase) score += 1;
  if (checks.numbers) score += 1;
  if (checks.special) score += 1;
  if (checks.longLength) score += 1;
  
  if (score <= 2) return { score, label: 'Weak', color: 'bg-red-500', textColor: 'text-red-600' };
  if (score <= 3) return { score, label: 'Fair', color: 'bg-orange-500', textColor: 'text-orange-600' };
  if (score <= 4) return { score, label: 'Good', color: 'bg-yellow-500', textColor: 'text-yellow-600' };
  if (score <= 5) return { score, label: 'Strong', color: 'bg-green-500', textColor: 'text-green-600' };
  return { score, label: 'Very Strong', color: 'bg-emerald-600', textColor: 'text-emerald-600' };
};

const AuthPage = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [isAdminLogin, setIsAdminLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    mailing_address: "",
    password: "",
    confirmPassword: "",
    privacy_consent: false,
    is_rn_member: false
  });
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef();
  
  // Password strength calculation
  const passwordStrength = useMemo(() => 
    calculatePasswordStrength(formData.password), 
    [formData.password]
  );

  // Email normalization
  const normalizeEmail = (email) => email.toLowerCase().trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const response = await axios.post(`${API}/auth/login`, {
          email: normalizeEmail(formData.email),
          password: formData.password,
          is_admin_login: isAdminLogin
        });
        
        login(response.data.token, response.data.member);
        
        // Route based on user role - Admins to Admin Panel, KJs to KJ Console
        if (response.data.member.is_admin) {
          toast.success("Welcome, Administrator!");
          navigate("/admin");
        } else {
          // Non-admin users (KJs/Members) go to KJ Console
          toast.success("Welcome back!");
          navigate("/kj-console");
        }
      } else {
        // Registration validation
        if (!captchaToken) {
          toast.error("Please complete the CAPTCHA");
          setLoading(false);
          return;
        }
        
        if (formData.password !== formData.confirmPassword) {
          toast.error("Passwords do not match");
          setLoading(false);
          return;
        }
        
        if (formData.password.length < 6) {
          toast.error("Password must be at least 6 characters");
          setLoading(false);
          return;
        }
        
        if (!formData.privacy_consent) {
          toast.error("Please accept the Privacy Policy & PDPA");
          setLoading(false);
          return;
        }
        
        const response = await axios.post(`${API}/auth/register`, {
          name: formData.name,
          email: normalizeEmail(formData.email),
          phone: formData.phone,
          mailing_address: formData.mailing_address,
          password: formData.password,
          privacy_consent: formData.privacy_consent,
          is_rn_member: formData.is_rn_member
        });
        login(response.data.token, response.data.member);
        toast.success("Registration successful!");
        navigate("/kj-console");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${
      isAdminLogin ? 'bg-gray-900' : 'bg-white'
    }`}>
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate('/')}
          className={`mb-6 flex items-center gap-2 text-sm font-bold hover:underline ${
            isAdminLogin ? 'text-white' : 'text-black'
          }`}
          data-testid="back-home-btn"
        >
          <ArrowLeft size={16} />
          BACK TO HOME
        </button>

        <div className={`brutal-card ${isAdminLogin ? 'bg-gray-800 border-primary' : ''}`}>
          {/* Login Type Toggle - Only show on login page */}
          {isLogin && (
            <div className="flex gap-2 mb-6">
              <button
                type="button"
                onClick={() => setIsAdminLogin(false)}
                className={`flex-1 py-3 px-4 font-bold uppercase border-2 border-black flex items-center justify-center gap-2 transition-all ${
                  !isAdminLogin 
                    ? 'bg-secondary text-black' 
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="kj-login-tab"
              >
                <User size={18} />
                KJ
              </button>
              <button
                type="button"
                onClick={() => setIsAdminLogin(true)}
                className={`flex-1 py-3 px-4 font-bold uppercase border-2 border-black flex items-center justify-center gap-2 transition-all ${
                  isAdminLogin 
                    ? 'bg-primary text-white' 
                    : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
                data-testid="admin-login-tab"
              >
                <Shield size={18} />
                ADMIN
              </button>
            </div>
          )}

          <div className="flex items-center gap-3 mb-6">
            <div className={`w-12 h-12 border-2 border-black flex items-center justify-center ${
              isAdminLogin ? 'bg-primary' : 'bg-primary'
            }`}>
              {isAdminLogin ? (
                <Shield className="w-6 h-6 text-white" strokeWidth={3} />
              ) : (
                <Music className="w-6 h-6 text-white" strokeWidth={3} />
              )}
            </div>
            <div>
              <h1 className={`text-2xl font-syne font-bold ${isAdminLogin ? 'text-white' : ''}`}>
                {isLogin ? (isAdminLogin ? "ADMIN LOGIN" : "STAFF & KJ LOGIN") : "KJ REGISTRATION"}
              </h1>
              {isAdminLogin && isLogin && (
                <p className="text-xs text-gray-400 mt-1">Authorized personnel only</p>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
            {!isLogin && (
              <div>
                <label className={`block text-sm font-bold mb-2 ${isAdminLogin ? 'text-white' : ''}`}>Name</label>
                <input
                  type="text"
                  name="name"
                  autoComplete="name"
                  className="brutal-input w-full"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required={!isLogin}
                  data-testid="name-input"
                />
              </div>
            )}

            <div>
              <label className={`block text-sm font-bold mb-2 ${isAdminLogin ? 'text-white' : ''}`}>Email</label>
              <input
                type="email"
                name="email"
                autoComplete="email"
                className={`brutal-input w-full ${isAdminLogin ? 'bg-white text-black' : ''}`}
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                placeholder={isAdminLogin ? "admin@example.com" : ""}
                data-testid="email-input"
              />
            </div>

            {!isLogin && (
              <>
                <div>
                  <label className={`block text-sm font-bold mb-2 ${isAdminLogin ? 'text-white' : ''}`}>Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    autoComplete="tel"
                    className="brutal-input w-full"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required={!isLogin}
                    data-testid="phone-input"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-bold mb-2 ${isAdminLogin ? 'text-white' : ''}`}>Mailing Address</label>
                  <textarea
                    name="address"
                    autoComplete="street-address"
                    className="brutal-input w-full h-20 resize-none"
                    value={formData.mailing_address}
                    onChange={(e) => setFormData({...formData, mailing_address: e.target.value})}
                    required={!isLogin}
                    data-testid="address-input"
                  />
                </div>
              </>
            )}

            <div>
              <label className={`block text-sm font-bold mb-2 ${isAdminLogin ? 'text-white' : ''}`}>Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete={isLogin ? "current-password" : "new-password"}
                  className={`brutal-input w-full pr-10 ${isAdminLogin ? 'bg-white text-black' : ''}`}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              
              {/* Password Strength Meter - Only show during registration */}
              {!isLogin && formData.password && (
                <div className="mt-2">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                        style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
                      />
                    </div>
                    <span className={`text-xs font-bold ${passwordStrength.textColor}`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Use 8+ chars with uppercase, lowercase, numbers & symbols
                  </p>
                </div>
              )}
            </div>

            {!isLogin && (
              <div>
                <label className={`block text-sm font-bold mb-2 ${isAdminLogin ? 'text-white' : ''}`}>Confirm Password</label>
                <input
                  type="password"
                  name="confirm-password"
                  autoComplete="new-password"
                  className="brutal-input w-full"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  required={!isLogin}
                  minLength={6}
                  data-testid="confirm-password-input"
                />
              </div>
            )}

            {!isLogin && (
              <>
                {/* KJ Role Selection */}
                <div className="p-4 bg-gray-50 border-2 border-gray-300 rounded space-y-3">
                  <p className="font-bold text-sm">Select Your KJ Role:</p>
                  <div className="space-y-2">
                    <label className="flex items-center gap-3 p-2 bg-white border rounded cursor-pointer hover:bg-green-50">
                      <input
                        type="radio"
                        name="kj_role"
                        className="w-4 h-4"
                        checked={!formData.is_rn_member}
                        onChange={() => setFormData({...formData, is_rn_member: false})}
                      />
                      <span className="font-bold text-green-700">External KJ</span>
                    </label>
                    <label className="flex items-center gap-3 p-2 bg-white border rounded cursor-pointer hover:bg-yellow-50">
                      <input
                        type="radio"
                        name="kj_role"
                        className="w-4 h-4"
                        checked={formData.is_rn_member}
                        onChange={() => setFormData({...formData, is_rn_member: true})}
                      />
                      <span className="font-bold text-yellow-700">RN Member</span>
                    </label>
                  </div>
                  <p className="text-xs text-orange-600 italic">⚠️ Your role selection is subject to Admin verification</p>
                </div>
                
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={formData.privacy_consent}
                    onChange={(e) => setFormData({...formData, privacy_consent: e.target.checked})}
                    required={!isLogin}
                    data-testid="privacy-checkbox"
                  />
                  <label className={`text-sm ${isAdminLogin ? 'text-white' : ''}`}>
                    I have read and agree to the Privacy Policy and PDPA compliance terms
                  </label>
                </div>
                
                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                    onChange={(token) => setCaptchaToken(token)}
                    data-testid="recaptcha"
                  />
                </div>
              </>
            )}

            <button
              type="submit"
              className={`w-full py-4 font-bold uppercase border-4 border-black transition-all ${
                isAdminLogin 
                  ? 'bg-primary text-white hover:opacity-90' 
                  : 'brutal-btn-primary'
              }`}
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? "LOADING..." : (isLogin ? (isAdminLogin ? "ADMIN LOGIN" : "LOGIN") : "REGISTER")}
            </button>
          </form>

          <div className="mt-6 text-center space-y-3">
            {isLogin && (
              <Link
                to="/password-reset"
                state={{ isAdminReset: isAdminLogin }}
                className={`block text-sm font-bold hover:underline ${isAdminLogin ? 'text-primary' : 'text-primary'}`}
                data-testid="forgot-password-link"
              >
                {isAdminLogin ? "Admin Password Reset" : "Forgot Password?"}
              </Link>
            )}
            
            {/* Only show "Need an account?" for KJ login, NOT for Admin */}
            {!isAdminLogin && (
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setIsAdminLogin(false);  // Ensure admin mode is off when switching
                }}
                className={`text-sm font-bold hover:underline ${isAdminLogin ? 'text-gray-400' : ''}`}
                data-testid="toggle-auth-btn"
              >
                {isLogin ? "Need an account? REGISTER" : "Already have an account? LOGIN"}
              </button>
            )}
            
            {/* For Admin tab, show a different message */}
            {isAdminLogin && isLogin && (
              <p className="text-xs text-gray-500 mt-2">
                Admin accounts are created by existing administrators only.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
